import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AgricolaService } from '../../../../services/agricola.service';
import { MeService } from '../../../../services/me.service';

export interface CancelDialogData {
  policyNumber: string;
  assistanceCode: number;
  memberId: number;
  frontingId: number;
  planName: string;
  assistanceSaleCode: string;
}

@Component({
  selector: 'app-agricola-cancel-dialog',
  templateUrl: './agricola-cancel-dialog.component.html',
  styleUrls: ['./agricola-cancel-dialog.component.css'],
  standalone: false
})
export class AgricolaCancelDialogComponent implements OnInit {

  form!: FormGroup;
  isSubmitting = false;

  typologyOptions:    { value: string; label: string }[] = [];
  subtypologyOptions: { value: string; label: string }[] = [];
  agencyOptions:      { value: string; label: string }[] = [];

  private subtypologiesMap = new Map<number, { value: string; label: string }[]>();

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AgricolaCancelDialogComponent>,
    private agricolaService: AgricolaService,
    private meService: MeService,
    @Inject(MAT_DIALOG_DATA) public data: CancelDialogData
  ) {}

  ngOnInit(): void {
    const today = new Date().toISOString().split('T')[0];

    this.form = this.fb.group({
      assistanceCode:      [{ value: this.data.policyNumber, disabled: true }],
      cancellationDate:    [{ value: today, disabled: true }],
      typologyId:          [null, Validators.required],
      subtypologyId:       [null, Validators.required],
      typologyIdToSend:    [''],
      subtypologyIdToSend: [''],
      notes:               ['Se CANCELA el seguro a solicitud del afiliado', Validators.required],
      uidViciDial:         [{ value: '', disabled: true }],
      agencyCode:          [null]
    });

    this.loadCatalogEquivalences();
    this.loadAgencies();
    this.subscribeToFormChanges();
  }

  private loadAgencies(): void {
    this.agricolaService.getAgencies().subscribe({
      next: (res: any) => {
        const raw = res?.data?.data ?? res?.data ?? res;
        const rows: any[] = Array.isArray(raw) ? raw : [];
        this.agencyOptions = rows
          .filter(r => r?.code)
          .map(r => ({
            value: String(r.code),
            label: String(r.description_es || r.description_en || r.code)
          }));
      }
    });
  }

  private loadCatalogEquivalences(): void {
    this.agricolaService.getCatalogEquivalences().subscribe({
      next: (res: any) => {
        const raw = res?.data?.data ?? res?.data ?? res;
        const items: any[] = Array.isArray(raw) ? raw : [];
        const apoloEntry = items.find(
          (e: any) => e.value_ === 'crm_typology_apolo' && e.to_ === '10'
        );
        if (!apoloEntry?.typologies) return;

        this.typologyOptions = apoloEntry.typologies.map((t: any) => ({
          value: String(t.id),
          label: t.description
        }));

        apoloEntry.typologies.forEach((t: any) => {
          this.subtypologiesMap.set(
            t.id,
            (t.subtypologies ?? []).map((s: any) => ({ value: String(s.id), label: s.description }))
          );
        });

        const cancelEntry = apoloEntry.typologies.find(
          (t: any) => t.description === 'Cancelación'
        );
        if (cancelEntry) {
          this.form.get('typologyId')!.setValue(String(cancelEntry.id));
          this.form.get('typologyId')!.disable();
          this.form.get('typologyIdToSend')!.setValue(cancelEntry.description);
        }
      }
    });
  }

  private subscribeToFormChanges(): void {
    this.form.get('typologyId')!.valueChanges.subscribe(id => {
      this.subtypologyOptions = this.subtypologiesMap.get(Number(id)) ?? [];
      this.form.get('subtypologyId')!.setValue(null, { emitEvent: false });
      this.form.get('subtypologyIdToSend')!.setValue('', { emitEvent: false });
      const label = this.typologyOptions.find(o => o.value === String(id))?.label ?? '';
      this.form.get('typologyIdToSend')!.setValue(label, { emitEvent: false });
    });

    this.form.get('subtypologyId')!.valueChanges.subscribe(id => {
      const label = this.subtypologyOptions.find(o => o.value === String(id))?.label ?? '';
      this.form.get('subtypologyIdToSend')!.setValue(label, { emitEvent: false });
    });
  }

  onSubmit(): void {
    if (this.form.invalid) return;
    const f = this.form.getRawValue();
    const today = new Date().toISOString().split('T')[0];

    const payload = {
      country_id:           4,
      sponsor_id:           'ESR98',
      fronting_id:          this.data.frontingId,
      username:             this.meService.currentUser?.email ?? '',
      userId:               this.meService.currentUser?.id   ?? '',
      guid:                 '',
      ucontact_did:         '',
      account_id:           this.data.memberId,
      transaction:          'assistance_cancelation',
      transaction_type:     'CLC',
      transaction_date:     today,
      typology_id:          f.typologyId   ? Number(f.typologyId)   : null,
      subtypology_id:       f.subtypologyId ? Number(f.subtypologyId) : null,
      typology_id_to_send:    f.typologyIdToSend    || '',
      subtypology_id_to_send: f.subtypologyIdToSend || '',
      assistance_code:      this.data.assistanceCode,
      cancellation_date:    f.cancellationDate,
      notes:                f.notes || '',
      UidViciDial:          f.uidViciDial || '',
      agency_id:            f.agencyCode  || '',
      agency_name:          this.agencyOptions.find(a => a.value === f.agencyCode)?.label || '',
      agency_user:          this.meService.currentUser?.name || this.meService.currentUser?.email || '',
      agency_user_id:       null,
      assistance_sale_code: this.data.assistanceSaleCode,
      elapsedTime:          0,
      plan:                 this.data.planName
    };

    this.isSubmitting = true;
    this.agricolaService.chubbTransaction(payload).subscribe({
      next: () => { this.isSubmitting = false; this.dialogRef.close({ success: true }); },
      error: () => { this.isSubmitting = false; this.dialogRef.close({ success: false }); }
    });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
